import {
  User,
  W9BusinessTaxClassifications,
  W9FormData,
  W9TaxClassifications,
  W9TinValues,
} from '@bybeam/platform-types';
import {
  SimpleValidationFieldConfig,
  conditionallyRequiredField,
  defaultRequiredErrorMessage,
  optionalField,
  requiredField,
} from '@platform-ui-common/hooks/useForm/index.js';

export const BUSINESS_TAX_CLASSIFICATIONS: Array<{
  label: string;
  value: W9BusinessTaxClassifications;
}> = [
  {
    label: 'Sole Proprietor or Single-member LLC',
    value: W9BusinessTaxClassifications.INDIVIDUAL_SOLE_PROPIETOR,
  },
  {
    label: 'C Corporation',
    value: W9BusinessTaxClassifications.C_CORPORATION,
  },
  {
    label: 'S Corporation',
    value: W9BusinessTaxClassifications.S_CORPORATION,
  },
  {
    label: 'Partnership',
    value: W9BusinessTaxClassifications.PARTNERSHIP,
  },
  {
    label: 'Trust/Estate',
    value: W9BusinessTaxClassifications.TRUST_ESTATE,
  },
  {
    label: 'Limited liability company (LLC)',
    value: W9BusinessTaxClassifications.LLC,
  },
  {
    label: 'Other',
    value: W9BusinessTaxClassifications.OTHER,
  },
];
export const signatureValidation = (
  user: User,
): { signature: SimpleValidationFieldConfig<W9FormData> } => ({
  signature: requiredField(
    (form: W9FormData) =>
      form.signature.toLowerCase() !== user.name.toLowerCase() && `Please enter "${user.name}"`,
    defaultRequiredErrorMessage('Electronic Signature'),
  ),
});

const commonTaxFormClassificationFields = {
  taxClassification: undefined,
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: undefined,
  zip: undefined,
  ein: undefined,
  signature: '',
};
const individualTaxFormClassificationFields = {
  tin: undefined,
  ssn: undefined,
};
const businessTaxFormClassificationFields = {
  businessTaxClassification: undefined,
  llcBusinessTaxClassification: undefined,
  otherEntityType: '',
  dba: '',
  exemptPayeeCode: undefined,
  exemptFATCACode: undefined,
};

export const commonTaxFormClassificationConfig = {
  taxClassification: requiredField(undefined, defaultRequiredErrorMessage('Tax Classification')),
  name: requiredField(undefined, defaultRequiredErrorMessage('Name')),
  addressLine1: requiredField(undefined, defaultRequiredErrorMessage('Street Address')),
  addressLine2: optionalField(),
  city: requiredField(undefined, defaultRequiredErrorMessage('City')),
  state: requiredField(undefined, defaultRequiredErrorMessage('State')),
  zip: requiredField(undefined, defaultRequiredErrorMessage('Zip Code')),
  ein: conditionallyRequiredField(
    (form: W9FormData) =>
      form.ein && form.ein.replaceAll('-', '')?.length !== 9 && 'Please enter a 9 digit number',
    (form: W9FormData) =>
      form.taxClassification === W9TaxClassifications.BUSINESS ||
      (form.taxClassification === W9TaxClassifications.INDIVIDUAL && form.tin === W9TinValues.EIN),
    defaultRequiredErrorMessage('Employer Identification Number'),
  ),
};
export const individualTaxFormClassificationConfig = {
  tin: conditionallyRequiredField(
    undefined,
    (form: W9FormData) => form.taxClassification === W9TaxClassifications.INDIVIDUAL,
    defaultRequiredErrorMessage('Taxpayer Identification Number'),
  ),
  ssn: conditionallyRequiredField(
    (form: W9FormData) =>
      form.ssn && form.ssn.replaceAll('-', '')?.length !== 9 && 'Please enter a 9 digit number',
    (form: W9FormData) =>
      form.taxClassification === W9TaxClassifications.INDIVIDUAL && form.tin === W9TinValues.SSN,
    defaultRequiredErrorMessage('Social security number'),
  ),
};
export const businessTaxFormClassificationConfig = {
  businessTaxClassification: conditionallyRequiredField(
    undefined,
    (form: W9FormData) => form.taxClassification === W9TaxClassifications.BUSINESS,
    defaultRequiredErrorMessage('Business Tax Classification'),
  ),
  llcBusinessTaxClassification: conditionallyRequiredField(
    undefined,
    (form: W9FormData) =>
      form.taxClassification === W9TaxClassifications.BUSINESS &&
      form.businessTaxClassification === BUSINESS_TAX_CLASSIFICATIONS[5].value,
    defaultRequiredErrorMessage('LLC Tax Classification'),
  ),
  otherEntityType: conditionallyRequiredField(
    undefined,
    (form: W9FormData) =>
      form.taxClassification === W9TaxClassifications.BUSINESS &&
      form.businessTaxClassification === BUSINESS_TAX_CLASSIFICATIONS[6].value,
    defaultRequiredErrorMessage('Other Entity Type'),
  ),
  dba: optionalField(),
  exemptPayeeCode: optionalField(),
  exemptFATCACode: optionalField(),
};

export const initialData = {
  ...individualTaxFormClassificationFields,
  ...businessTaxFormClassificationFields,
  ...commonTaxFormClassificationFields,
};
